import React, { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import logger from "../../utils/logger";
import GameSearchDropdown from "../common/GameSearchDropdown";
import "./AdminBggHotnessSimilar.css";

const LIMIT = 25;

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function GameSearchInput({ value, onSelect, placeholder }) {
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setSearchQuery(value || "");
    setSearchResults([]);
    setIsVisible(false);
  }, [value]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await httpRequest(
          `${API_BASE}/games/search?q=${encodeURIComponent(q)}&limit=10`,
          { method: "GET", headers: authService.getAuthHeaders() },
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.games || []);
        }
      } catch (e) {
        logger.error("Error searching games", e);
      } finally {
        setIsLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleGameSelect = (game) => {
    onSelect(game.id, game.name);
    setSearchQuery(game.name);
    setIsVisible(false);
  };

  return (
    <GameSearchDropdown
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      isVisible={isVisible}
      onVisibilityChange={setIsVisible}
      searchResults={searchResults}
      isLoading={isLoading}
      onGameSelect={handleGameSelect}
      placeholder={placeholder}
      showNoResultsMessage={false}
      containerStyle={{ minWidth: "250px" }}
    />
  );
}

function AdminBggHotnessSimilar() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [token, setToken] = useState("");
  const [showSync, setShowSync] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState({
    bgg_id: "",
    bgg_rank: "",
    bgg_name: "",
    similar_1: null,
    similar_1_name: "",
    similar_2: null,
    similar_2_name: "",
    similar_3: null,
    similar_3_name: "",
  });
  const [syncing, setSyncing] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const show = useCallback((text, type = "") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(LIMIT),
        offset: String(offset),
      });
      if (statusFilter) params.append("status", statusFilter);
      if (search.trim()) params.append("search", search.trim());
      const res = await httpRequest(`${API_BASE}/admin/similar-games?${params.toString()}`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setRows(data.items || []);
        setTotal(data.total || 0);
      } else {
        const err = await res.text();
        show(`Failed to load table: ${err}`, "error");
      }
    } catch (e) {
      logger.error("Error loading similar_games", e);
      show(`Error loading table: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [offset, statusFilter, search, show]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const res = await httpRequest(`${API_BASE}/admin/data-updates/status/${taskId}`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setTaskStatus(data);
          if (["completed", "failed", "stopped"].includes(data.status)) {
            clearInterval(interval);
            setSyncing(false);
            show(`Sync ${data.status}: ${data.message || ""}`, data.status === "completed" ? "success" : "error");
            fetchRows();
          }
        }
      } catch (e) {
        logger.error("Error polling sync status", e);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [taskId, fetchRows, show]);

  const handleStatusChange = async (bggId, newStatus) => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games/${bggId}`, {
        method: "PATCH",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        show("Status updated", "success");
        fetchRows();
      } else {
        const err = await res.text();
        show(`Update failed: ${err}`, "error");
      }
    } catch (e) {
      show(`Update failed: ${e.message}`, "error");
    }
  };

  const handleRefresh = async (bggId) => {
    if (!token.trim()) {
      show("Please enter a Pista production token before refreshing", "error");
      setShowSync(true);
      return;
    }
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games/${bggId}/refresh`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: token.trim() }),
      });
      if (res.ok) {
        show("Refreshed", "success");
        fetchRows();
      } else {
        const err = await res.text();
        show(`Refresh failed: ${err}`, "error");
      }
    } catch (e) {
      show(`Refresh failed: ${e.message}`, "error");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games/sync`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setTaskId(data.task_id);
        show(`Sync started: ${data.task_id}`, "success");
      } else {
        const err = await res.text();
        show(`Sync failed to start: ${err}`, "error");
        setSyncing(false);
      }
    } catch (e) {
      show(`Sync failed: ${e.message}`, "error");
      setSyncing(false);
    }
  };

  const handleGenerate = async () => {
    if (!token.trim()) {
      show("Please enter a Pista production token before generating", "error");
      setShowSync(true);
      return;
    }
    setSyncing(true);
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games/generate`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: token.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setTaskId(data.task_id);
        show(`Generate started: ${data.task_id}`, "success");
      } else {
        const err = await res.text();
        show(`Generate failed to start: ${err}`, "error");
        setSyncing(false);
      }
    } catch (e) {
      show(`Generate failed: ${e.message}`, "error");
      setSyncing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games/download-json`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "similar_games.json";
        a.click();
        window.URL.revokeObjectURL(url);
        show("Download started", "success");
      } else {
        const err = await res.text();
        show(`Download failed: ${err}`, "error");
      }
    } catch (e) {
      show(`Download failed: ${e.message}`, "error");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const bggId = parseInt(newRow.bgg_id, 10);
    if (!bggId || Number.isNaN(bggId)) {
      show("BGG ID must be a number", "error");
      return;
    }
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bgg_id: bggId,
          bgg_rank: newRow.bgg_rank ? parseInt(newRow.bgg_rank, 10) : null,
          bgg_name: newRow.bgg_name || null,
          similar_1: newRow.similar_1,
          similar_2: newRow.similar_2,
          similar_3: newRow.similar_3,
        }),
      });
      if (res.ok) {
        show("Row added", "success");
        setNewRow({
          bgg_id: "",
          bgg_rank: "",
          bgg_name: "",
          similar_1: null,
          similar_1_name: "",
          similar_2: null,
          similar_2_name: "",
          similar_3: null,
          similar_3_name: "",
        });
        fetchRows();
      } else {
        const err = await res.text();
        show(`Add row failed: ${err}`, "error");
      }
    } catch (e) {
      show(`Add row failed: ${e.message}`, "error");
    }
  };

  const handleSimilarChange = async (bggId, field, id, name) => {
    try {
      const res = await httpRequest(`${API_BASE}/admin/similar-games/${bggId}`, {
        method: "PATCH",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ [field]: id }),
      });
      if (res.ok) {
        show(`${name || "Game"} set as ${field}`, "success");
        fetchRows();
      } else {
        const err = await res.text();
        show(`Update failed: ${err}`, "error");
      }
    } catch (e) {
      show(`Update failed: ${e.message}`, "error");
    }
  };

  return (
    <div className="bgs-admin">
      <div className="bgs-header">
        <h2 className="bgs-title">BGG Hotness Similar Games</h2>
        <div className="bgs-actions">
          <button className="bgs-btn" onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Hide Add Row" : "Add Row"}
          </button>
          <button className="bgs-btn" onClick={() => setShowSync((v) => !v)}>
            {showSync ? "Hide Sync / Generate" : "Sync / Generate"}
          </button>
          <button className="bgs-btn" onClick={handleDownload}>
            Download JSON
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`bgs-message bgs-message--${message.type}`}>{message.text}</div>
      )}

      {showSync && (
        <div className="bgs-form">
          <input
            className="bgs-input"
            type="password"
            placeholder="Pista production token (for Refresh / Generate)"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <button
            className="bgs-btn bgs-btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? "Syncing…" : "Sync BGG Hotness"}
          </button>
          <button
            className="bgs-btn bgs-btn-primary"
            onClick={handleGenerate}
            disabled={syncing}
          >
            {syncing ? "Generating…" : "Generate Pending"}
          </button>
          {taskStatus && (
            <div className="bgs-message">
              {taskStatus.message || taskStatus.status} ({taskStatus.progress || 0}%)
            </div>
          )}
        </div>
      )}

      {showAdd && (
        <form className="bgs-form" onSubmit={handleCreate}>
          <GameSearchInput
            value={newRow.bgg_name}
            onSelect={(id, name) => setNewRow({ ...newRow, bgg_id: id, bgg_name: name })}
            placeholder="Search reference game"
          />
          <input
            className="bgs-input"
            type="number"
            placeholder="BGG ID"
            value={newRow.bgg_id}
            readOnly
            required
          />
          <input
            className="bgs-input"
            type="number"
            placeholder="BGG rank (optional)"
            value={newRow.bgg_rank}
            onChange={(e) => setNewRow({ ...newRow, bgg_rank: e.target.value })}
          />
          <GameSearchInput
            value={newRow.similar_1_name}
            onSelect={(id, name) => setNewRow({ ...newRow, similar_1: id, similar_1_name: name })}
            placeholder="Search Similar 1"
          />
          <GameSearchInput
            value={newRow.similar_2_name}
            onSelect={(id, name) => setNewRow({ ...newRow, similar_2: id, similar_2_name: name })}
            placeholder="Search Similar 2"
          />
          <GameSearchInput
            value={newRow.similar_3_name}
            onSelect={(id, name) => setNewRow({ ...newRow, similar_3: id, similar_3_name: name })}
            placeholder="Search Similar 3"
          />
          <button className="bgs-btn bgs-btn-primary" type="submit">
            Create Row
          </button>
        </form>
      )}

      <div className="bgs-filters">
        <input
          className="bgs-input"
          type="text"
          placeholder="Search name or BGG ID"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
        />
        <select
          className="bgs-select"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
        >
          <option value="">All statuses</option>
          <option value="pending">pending</option>
          <option value="done">done</option>
          <option value="excluded">excluded</option>
          <option value="error">error</option>
        </select>
      </div>

      <div className="bgs-table-wrap">
        <table className="bgs-table">
          <thead>
            <tr>
              <th>BGG ID</th>
              <th>Rank</th>
              <th>Name</th>
              <th>Reference ID</th>
              <th>Status</th>
              <th>Similar 1</th>
              <th>Similar 2</th>
              <th>Similar 3</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan="10" style={{ textAlign: "center" }}>
                  No rows found.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.bgg_id}>
                <td>{row.bgg_id}</td>
                <td>{row.bgg_rank ?? "—"}</td>
                <td>{row.bgg_name}</td>
                <td>{row.reference_game_id}</td>
                <td>
                  <select
                    className="bgs-status-select"
                    value={row.status}
                    onChange={(e) => handleStatusChange(row.bgg_id, e.target.value)}
                  >
                    <option value="pending">pending</option>
                    <option value="done">done</option>
                    <option value="excluded">excluded</option>
                    <option value="error">error</option>
                  </select>
                </td>
                <td>
                  <GameSearchInput
                    value={row.similar_1_name ?? ""}
                    onSelect={(id, name) => handleSimilarChange(row.bgg_id, "similar_1", id, name)}
                    placeholder="Search..."
                  />
                </td>
                <td>
                  <GameSearchInput
                    value={row.similar_2_name ?? ""}
                    onSelect={(id, name) => handleSimilarChange(row.bgg_id, "similar_2", id, name)}
                    placeholder="Search..."
                  />
                </td>
                <td>
                  <GameSearchInput
                    value={row.similar_3_name ?? ""}
                    onSelect={(id, name) => handleSimilarChange(row.bgg_id, "similar_3", id, name)}
                    placeholder="Search..."
                  />
                </td>
                <td>{formatDate(row.updated_at)}</td>
                <td>
                  <button className="bgs-btn" onClick={() => handleRefresh(row.bgg_id)}>
                    Refresh
                  </button>
                </td>
              </tr>
            ))}
            {loading && (
              <tr>
                <td colSpan="10" style={{ textAlign: "center" }}>
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bgs-pagination">
        <button
          className="bgs-btn"
          onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
          disabled={offset === 0}
        >
          Previous
        </button>
        <span>
          {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
        </span>
        <button
          className="bgs-btn"
          onClick={() => setOffset((o) => o + LIMIT)}
          disabled={offset + LIMIT >= total}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default AdminBggHotnessSimilar;
